import { useState, useEffect, useContext } from 'react';
import {
  DevHubTextField,
  DevHubFormControl,
  DevHubAutocomplete,
} from '@devxdiscover/devhub-ui';
import { gql, useLazyQuery } from '@apollo/client';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import { CustomFieldExtensionSchema } from '@backstage/plugin-scaffolder-react';
import { slugify } from '../../util/stringFormat';
import { UserContext } from '../../contexts/UserContext';

/**
 * The schema for the {@link OwnedITRCAppPicker} properties.
 */
export const OwnedITRCAppPickerSchema: CustomFieldExtensionSchema = {
  returnValue: {
    type: 'object',
    properties: {
      applicationId: {
        type: 'number',
        title: 'Application Id',
        description: 'The ID of the application',
      },
      name: {
        type: 'string',
        title: 'Application Name',
        description: 'The name of the application',
      },
      roles: {
        type: 'array',
        title: 'Role List',
        description:
          'A list of roles the user has associated with the Application',
      },
      appslug: {
        type: 'string',
        title: 'App Slug',
        description: 'The slug of the application',
      },
    },
    required: ['applicationId', 'name', 'rolename'],
  },
};

type App = {
  name: string;
  applicationId: string;
  appslug?: string;
  status: string;
};

const GetUserRoleBasedApplicationsQuery = gql`
  query GetUserRoleBasedApplications(
    $login: String!
    $filter: ResourceCatalogUserApplicationFilter
  ) {
    identity {
      user(login: $login) {
        login
        resources {
          applicationsPaged(filter: $filter) {
            applications {
              name
              applicationId
              status
            }
          }
        }
      }
    }
  }
`;

/**
 * The input props that can be specified under `ui:options` for the
 * `OwnedITRCAppPicker` field extension.
 *
 * @public
 */
export type OwnedITRCAppState = FieldExtensionComponentProps<App | undefined>;

/**
 * The underlying component that is rendered in the form for the `OwnedITRCAppPicker`
 * field extension.
 * @param props - The properties passed to the component.
 * @public
 */
export const OwnedITRCAppPicker = (props: OwnedITRCAppState) => {
  const { formData, onChange, rawErrors, idSchema, required } = props;
  const [apps, setApps] = useState<App[]>([]);
  const { login } = useContext(UserContext);

  const [fetchApplications, { data, loading, error }] = useLazyQuery(
    GetUserRoleBasedApplicationsQuery,
    {
      errorPolicy: 'all',
      variables: {
        login: login,
        filter: {
          havingRoles: [
            'owner',
            'assistant_owner',
            'approver',
            'devops_remediation',
            'developer',
          ],
        },
      },
    },
  );

  const filterRetired = (app: App): boolean => {
    return app.status !== 'Retired';
  };

  useEffect(() => {
    if (login) {
      fetchApplications();
    }

    if (data) {
      const appsCopy: App[] =
        data?.identity?.user?.resources?.applicationsPaged?.applications?.map(
          (app: App) => Object.assign({}, app, { appslug: slugify(app.name) }),
        ) ?? [];

      appsCopy.sort((a: App, b: App) => a?.name?.localeCompare(b?.name));
      setApps(appsCopy.filter(filterRetired));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login, data]);

  const onSelect = (_: any, value: App | null) => {
    if (!value) {
      return;
    }

    onChange(value);
  };

  return (
    <DevHubFormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
    >
      {error ? (
        <DevHubTextField
          error
          label="Error"
          defaultValue="No Applications Available"
          helperText="Failed to fetch application details."
          variant="outlined"
        />
      ) : (
        <DevHubAutocomplete
          id={idSchema?.$id}
          value={formData}
          loading={loading}
          loadingText="Loading.."
          options={apps}
          getOptionLabel={option => option?.name}
          onChange={onSelect}
          autoSelect
          renderInput={params => (
            <DevHubTextField
              {...params}
              label={
                apps?.length > 0
                  ? 'Applications available'
                  : 'No assigned applications, please reach out to your team.'
              }
              margin="normal"
              helperText="Applications with ITRC role associated with user"
              variant="outlined"
              required
              InputProps={params.InputProps}
            />
          )}
        />
      )}
    </DevHubFormControl>
  );
};
